/* eslint-disable max-statements */
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    createApi,
    fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { Mutex, MutexInterface } from 'async-mutex';
import { toast } from 'react-toastify';

import { hideLoader, showLoader } from '../components/loadingSpinner/loadingspinner';
import networkToastService from '../components/networkStatus/networkToastService';
import { API_URI } from '../constants';
import { logOut, setAuthCredentials } from '../pages/login/authSlice';
import { sendRefreshTokenRequest } from '../pages/login/authToken';
import { RootState } from '../store/store';

const mutex = new Mutex();

/**
 * Detects the case where an API call receives HTTP 200 but the body is the
 * SPA's own index.html shell instead of JSON — typically caused by a VPN
 * or proxy drop that intercepts the API route without failing the request
 * at the network layer (so it doesn't surface as FETCH_ERROR).
 */
const isHtmlShellResponse = (error: FetchBaseQueryError): boolean =>
    error.status === 'PARSING_ERROR'
    && typeof error.data === 'string'
    && error.data.trim().toLowerCase().startsWith('<!doctype html');

const baseQuery = fetchBaseQuery({
    baseUrl: API_URI,
    prepareHeaders: (headers, { getState }) => {
        // By default, if we have a token in the store, let's use that for authenticated requests
        const token = (getState() as RootState).auth.token;
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    //exclude getMoodmeterNameUsersGroupMood from showing loader
    if (api.endpoint !== 'getMoodmeterNameUsersGroupMood') {
        showLoader();
    }

    await mutex.waitForUnlock(); // Wait until the mutex is available without locking it

    // eslint-disable-next-line @typescript-eslint/ban-types
    let result: QueryReturnValue<unknown, FetchBaseQueryError, {}> = await baseQuery(args, api, extraOptions);
    if (result.error) {
        // console.error('FETCH_ERROR', result.error);
        if (result.error.status === 'FETCH_ERROR') {
            networkToastService.showOffline();
        } else if (isHtmlShellResponse(result.error)) {
            // API route was intercepted and served the SPA shell (index.html)
            // instead of JSON, typically via a VPN/proxy drop that still
            // returns HTTP 200 — treat the same as a network failure.
            networkToastService.showOffline();
        } else if (result?.error?.status === 500) {
            toast.error('API Service Error!!');
        } else if (
            (result.error as any).originalStatus === 401 ||
            result?.error?.status === 401 ||
            result?.error?.status === 403
        ) {
            if (!mutex.isLocked()) {
                const release: MutexInterface.Releaser = await mutex.acquire();
                try {
                    const { auth } = api.getState() as RootState;
                    const refreshToken = auth.refreshToken;

                    if (refreshToken) {
                        const response = await sendRefreshTokenRequest(refreshToken);
                        const token = response[0].id_token ?? response[0].access_token; // prefer id_token for JWT Bearer validation
                        const expiresIn = response[0].expires_in;
                        const newRefreshToken = response[0].refresh_token ?? refreshToken; // PingOne rotates refresh tokens
                        const tokeobj = { token, refreshToken: newRefreshToken, expiresIn };

                        api.dispatch(setAuthCredentials(tokeobj));
                        result = await baseQuery(args, api, extraOptions); // Retry original request
                    } else {
                        toast.error('Your session has expired. Please login again.');
                        api.dispatch(logOut());
                        localStorage.clear();
                    }
                } catch (error) {
                    toast.error('Your session has expired. Please login again.');
                    console.log('TOKEN REQUEST ERROR', error);
                    api.dispatch(logOut());
                    localStorage.clear();
                } finally {
                    release();
                }
            } else {
                // A refresh is already in progress for another failed request.
                // Wait for it to finish, then retry this request with the new token.
                await mutex.waitForUnlock();
                result = await baseQuery(args, api, extraOptions);
            }
        } else {
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }
    // Successful response — connection is back, clear offline toast if showing
    if (!result.error) {
        networkToastService.showOnline();
    }

    setTimeout(() => {
        hideLoader();
    }, 200);
    return result;
};

const apiTagTypes = [
    'Disputes',
    'ScExAssignment',
    'ScStartDateOverride',
    'Approvals',
    'Delegates',
    'Corrective_action',
    'Coaching',
    'CoachingConversationModal',
    'CoachingToDo',
    'CoachingShoutOut',
    'Coaching_Metric',
    'CoachingGoal',
    'CoachingToDoView',
    'CoachingShoutOutCarousel',
    'Notification',
    'AdminNotification',
    'recalculation',
    'CoachingStrengthModal',
    'DirectReportees',
    'TeamPayoutApprovals',
    'HaltApprovals',
    'triad',
    'coaching_insight',
    'CourseAssignment',
    'StackRank',
    'ServiceNowApprovals',
    'CoachingActionPlan',
    'CoachingActionSession',
    'RollingMetricSummary',
    'PrepareAssociates',
    'PrepareAssociateMetrics',
    'AssociateActiveActionPlans',
    'AssociateHistoricalActionPlans',
];

export const apiSliceType = createApi({
    reducerPath: 'api',
    keepUnusedDataFor: 0,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
    baseQuery: baseQueryWithReauth,
    tagTypes: apiTagTypes,
    endpoints: () => ({}),
});

export const apiSlice = apiSliceType as any;
